import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, StatusPill } from "@/components/Primitives";
import DataTable from "@/components/DataTable";

export default function PANPHub() {
  const [agreements, setAgreements] = useState([]);
  const [queue, setQueue] = useState([]);
  const [flags, setFlags] = useState([]);
  const [perf, setPerf] = useState([]);
  const [form, setForm] = useState({ pa_np_name: "", state: "CA", supervision_type: "general", expiration_date: "" });

  const load = () => {
    api.get("/pa-np/agreements").then(r=>setAgreements(r.data));
    api.get("/pa-np/cosign-queue").then(r=>setQueue(r.data));
    api.get("/pa-np/scope-flags").then(r=>setFlags(r.data));
    api.get("/pa-np/performance").then(r=>setPerf(r.data));
  };
  useEffect(load, []);

  const addAgreement = async (e) => {
    e.preventDefault();
    await api.post("/pa-np/agreements", form);
    toast.success("Supervision agreement created");
    setForm({ pa_np_name: "", state: "CA", supervision_type: "general", expiration_date: "" });
    load();
  };

  const coSign = async (id) => {
    await api.post(`/pa-np/cosign/${id}`);
    toast.success("Co-signed");
    setQueue(q=>q.filter(x=>x.id!==id));
  };

  const agreementCols = [
    { key: "pa_np_name", label: "PA / NP" },
    { key: "state", label: "State" },
    { key: "supervision_type", label: "Type" },
    { key: "expiration_date", label: "Expires", render: (r)=>new Date(r.expiration_date).toLocaleDateString() },
    { key: "days_until_expiration", label: "Days", render: (r)=>(
      <span className={r.alert?"text-red-600 font-semibold":""}>{r.days_until_expiration} {r.alert && <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />}</span>
    )},
  ];

  const queueCols = [
    { key: "pa_np", label: "PA/NP" },
    { key: "patient", label: "Patient" },
    { key: "note_type", label: "Note Type" },
    { key: "priority", label: "Priority", render: (r)=><StatusPill status={r.priority} /> },
    { key: "submitted", label: "Submitted", render: (r)=>new Date(r.submitted).toLocaleString() },
    { key: "actions", label: "", sortable: false, render: (r)=>(
      <button onClick={()=>coSign(r.id)} className="text-xs px-2 py-1 rounded bg-brand-teal text-white inline-flex items-center gap-1" data-testid={`cosign-${r.id}`}>
        <CheckCircle2 className="w-3.5 h-3.5" /> Co-sign
      </button>
    )},
  ];

  const perfCols = [
    { key: "name", label: "PA/NP" },
    { key: "patients_seen_30d", label: "Patients (30d)" },
    { key: "doc_quality", label: "Doc Quality", render: (r)=>`${r.doc_quality}%` },
    { key: "revenue_30d", label: "Revenue", render: (r)=>`$${r.revenue_30d.toLocaleString()}` },
    { key: "cosign_compliance", label: "Co-sign Compliance", render: (r)=>`${r.cosign_compliance}%` },
  ];

  const flagCols = [
    { key: "pa_np", label: "PA/NP" },
    { key: "documented", label: "Documented" },
    { key: "state", label: "State" },
    { key: "scope_violation", label: "Issue" },
    { key: "severity", label: "Severity", render: (r)=><StatusPill status={r.severity} /> },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 13" title="PA & NP Collaboration Hub"
        subtitle="Supervision agreements, co-signature queue, scope compliance, and performance — all with audit trail." testid="panp-page" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="New Supervision Agreement" testid="panp-form">
          <form onSubmit={addAgreement} className="space-y-2">
            <input placeholder="PA / NP name" value={form.pa_np_name} onChange={(e)=>setForm({...form, pa_np_name:e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="panp-name" />
            <select value={form.state} onChange={(e)=>setForm({...form, state:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm">
              {["CA","TX","NY","FL","IL","WA","MA","CO"].map(s=><option key={s}>{s}</option>)}
            </select>
            <select value={form.supervision_type} onChange={(e)=>setForm({...form, supervision_type:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm">
              <option value="general">General supervision</option>
              <option value="direct">Direct supervision</option>
              <option value="collaborative">Collaborative practice</option>
            </select>
            <input type="date" required onChange={(e)=>setForm({...form, expiration_date: e.target.value+"T00:00:00Z"})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="panp-exp" />
            <button type="submit" className="w-full bg-brand-primary text-white py-2 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Create
            </button>
          </form>
        </SectionCard>

        <div className="lg:col-span-2">
          <SectionCard title="Supervision Agreements" testid="panp-agreements">
            <DataTable data={agreements} columns={agreementCols} searchKeys={["pa_np_name","state"]} testid="agreements-table" />
          </SectionCard>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Co-signature Queue" subtitle="Notes & orders awaiting physician sign-off" testid="cosign-queue">
          <DataTable data={queue} columns={queueCols} searchKeys={["pa_np","patient"]} testid="cosign-table" />
        </SectionCard>
        <SectionCard title="Scope of Practice Flags" subtitle="State-rules engine" testid="scope-flags">
          <DataTable data={flags} columns={flagCols} searchKeys={["pa_np","state"]} testid="scope-table" />
        </SectionCard>
      </div>

      <div className="mt-6">
        <SectionCard title="Performance & Productivity" testid="panp-perf">
          <DataTable data={perf} columns={perfCols} searchKeys={["name"]} testid="perf-table" />
        </SectionCard>
      </div>
    </>
  );
}
